import type { SkillProficiencyValue } from "shared";

/**
 * Wire → domain proficiency, for every endpoint that carries one.
 *
 * ⚠️ The backend stores this as an unconstrained `varchar` written by two paths
 * that disagree: `/v1/role-profiles/*` capitalises ("Intermediate"), while
 * manual skills keep whatever the client sent. `GET /v1/control-center/resource
 * /:id/skills` can therefore return **both casings in one array** — inherited
 * rows capitalised, manual rows lowercase (backend confirmed 2026-08-17).
 *
 * `SkillProficiencyValue` is the lowercase union, so reading any of these raw
 * yields values that no comparison in the UI matches. Normalise at the api
 * boundary and let everything downstream deal in the domain value.
 *
 * Unknown input falls back to "beginner" rather than leaking a bad string into
 * a typed field. Backend has agreed to align the columns behind a data
 * migration (PENDING-APIS RP-2) — when that lands, this becomes an identity
 * function and deletes cleanly from the one place it lives.
 */
export const fromApiProficiency = (
  value: string | null | undefined,
): SkillProficiencyValue => {
  const normalised = (value ?? "").trim().toLowerCase();
  return normalised === "expert" || normalised === "intermediate"
    ? normalised
    : "beginner";
};

/**
 * Domain → wire, for `/v1/role-profiles/*` only. Every other endpoint takes the
 * lowercase value as-is, so do not reach for this elsewhere.
 */
export const toApiProficiency = (value: SkillProficiencyValue): string =>
  value.charAt(0).toUpperCase() + value.slice(1);
